import { useEffect, useState } from "react";
import { Typography, Box, Paper, Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getCitizensOverview } from "../../services/apiCalls.js";
import { DataGrid } from "@mui/x-data-grid";
import { getRiskColor } from "../../services/Statistics/Risk/riskandRulesEnpoints.js";
import Layout from "../../layout/Layout.jsx";
import UserInfoModal from "../../components/Citizens/SubjectOverview/UserInfoModal.jsx";
import InfoTooltip from "../../components/Statistics/Risk/InfoTooltip.jsx";
import BiasAssessment from "../BiasAssessment/BiasAssessment.jsx";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

/**
 * Determine the status color and level based on the most recent data date.
 * Green: data within 7 days. Orange: 7-14 days. Red: 14+ days or no data.
 */
const getStatusInfo = (lastHealthData, lastQuestionnaire) => {
  const mostRecent = [lastHealthData, lastQuestionnaire]
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  if (!mostRecent) {
    return { color: "#F44336", level: 2 };
  }

  const daysSince = Math.floor(
    (new Date() - new Date(mostRecent)) / (1000 * 60 * 60 * 24)
  );

  if (daysSince < 7) return { color: "#4CAF50", level: 0 };
  if (daysSince < 14) return { color: "#FF9800", level: 1 };
  return { color: "#F44336", level: 2 };
};

/**
 * Format a date string to DD/MM/YYYY.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function Citizens() {
  const [usersTable, setUsersTable] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openBias, setOpenBias] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const { t } = useTranslation("citizens");

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        setLoading(true);
        const overviewData = await getCitizensOverview();

        const tableInfoUsers = overviewData.map((user) => {
          // Build riskData in the format getRiskColor expects
          const riskData = user.risk
            ? {
                id: user.risk.id,
                final_risk_level: user.risk.final_risk_level,
                updated_at: user.risk.updated_at,
              }
            : null;

          // Build ruleData in the format InfoTooltip expects:
          // [{ StratificationRulesModel: { rule_factor, rule_risk, activation_value } }]
          const ruleData =
            user.risk && user.risk.rules && user.risk.rules.length > 0
              ? user.risk.rules.map((r) => ({
                  StratificationRulesModel: {
                    rule_factor: r.rule_factor,
                    rule_risk: r.rule_risk,
                    activation_value: r.activation_value,
                  },
                }))
              : null;

          const statusInfo = getStatusInfo(
            user.last_health_data,
            user.last_questionnaire
          );

          // Determine which date is most recent for display
          const mostRecentDate = [user.last_health_data, user.last_questionnaire]
            .filter(Boolean)
            .sort((a, b) => new Date(b) - new Date(a))[0];

          return {
            id: user.id,
            subject_id: user.subject_id,
            sex: user.sex,
            risk: user.risk?.final_risk_level || null,
            hasRecommendations: !!user.has_pending_recommendations,
            age: user.age,
            height: user.height,
            weight: user.weight,
            riskData,
            ruleData,
            statusColor: statusInfo.color,
            statusLevel: statusInfo.level,
            lastUpdate: mostRecentDate,
            lastUpdateFormatted: formatDate(mostRecentDate),
            lastHealthData: user.last_health_data,
            lastQuestionnaire: user.last_questionnaire,
          };
        });

        setUsersTable(tableInfoUsers);
      } catch (error) {
        // console.log("Error fetching citizens overview:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsersData();
  }, []);

  const riskOptions = Array.from(new Set(usersTable?.map((item) => item.risk)));
  const columns = [
    {
      field: "statusColor",
      headerName: "",
      width: 50,
      align: "center",
      headerAlign: "center",
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      sortComparator: (v1, v2, param1, param2) => {
        return (
          param1.api.getRow(param1.id).statusLevel -
          param2.api.getRow(param2.id).statusLevel
        );
      },
      renderCell: (params) => {
        const user = params.row;
        const statusLabels = [
          t("status.active"),
          t("status.inactive7"),
          t("status.inactive14"),
        ];
        return (
          <Tooltip title={statusLabels[user.statusLevel]} arrow>
            <FiberManualRecordIcon
              sx={{
                color: user.statusColor,
                fontSize: "14px",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "subject_id",
      headerName: t("columns.subjectId"),
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "sex",
      headerName: t("columns.sex"),
      flex: 1,
      filterable: true,
      type: "singleSelect",
      align: "center",
      headerAlign: "center",
      valueOptions: [
        { value: "Male", label: t("sexOptions.male") },
        { value: "Female", label: t("sexOptions.female") },
      ],
      valueFormatter: (value) => {
        if (value === "Male") return t("sexOptions.male");
        if (value === "Female") return t("sexOptions.female");
        return value;
      },
    },
    {
      field: "risk",
      headerName: t("columns.risk"),
      flex: 1,
      type: "singleSelect",
      align: "center",
      headerAlign: "center",
      valueOptions: riskOptions,
      renderCell: (params) => {
        const user = params.row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography variant="body2">
              {user.risk != null ? user.risk : t("risk.notCalculated")}
            </Typography>
            {user.risk != null && (
              <InfoTooltip
                riskColor={() => getRiskColor(user.riskData)}
                ruleData={user.ruleData}
                basicUserInfo={user}
                riskData={user.riskData}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "hasRecommendations",
      headerName: t("columns.recommendations"),
      flex: 1,
      filterable: true,
      type: "singleSelect",
      valueOptions: [
        { value: true, label: t("recommendationStatus.available") },
        { value: false, label: t("recommendationStatus.notAvailable") },
      ],
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t("recommendationStatus.available")
              : t("recommendationStatus.notAvailable")
          }
          sx={{
            color: "white",
            bgcolor: params.value ? "primary.main" : "gray",
          }}
        />
      ),
    },
    {
      field: "lastUpdateFormatted",
      headerName: t("columns.lastUpdate"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      filterable: false,
      sortComparator: (v1, v2, param1, param2) => {
        const d1 = param1.api.getRow(param1.id).lastUpdate;
        const d2 = param2.api.getRow(param2.id).lastUpdate;
        if (!d1 && !d2) return 0;
        if (!d1) return 1;
        if (!d2) return -1;
        return new Date(d1) - new Date(d2);
      },
      renderCell: (params) => {
        const user = params.row;
        if (!user.lastUpdateFormatted) {
          return (
            <Typography variant="body2" color="text.secondary">
              {t("lastUpdate.noData")}
            </Typography>
          );
        }

        let source = "";
        if (user.lastHealthData && user.lastQuestionnaire) {
          source =
            new Date(user.lastHealthData) >= new Date(user.lastQuestionnaire)
              ? t("lastUpdate.healthData")
              : t("lastUpdate.questionnaire");
        } else if (user.lastHealthData) {
          source = t("lastUpdate.healthData");
        } else {
          source = t("lastUpdate.questionnaire");
        }

        return (
          <Tooltip title={source} arrow>
            <Typography variant="body2">{user.lastUpdateFormatted}</Typography>
          </Tooltip>
        );
      },
    },
  ];

  const handleRowClick = (params) => {
    setSelectedUser(params.row);
    setOpenModal(true);
  };
  const handleCloseBias = () => {
    setOpenBias(false);
  };
  return (
    <Layout>
      <Box sx={{ margin: "40px 30px" }}>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <Typography
            variant="h6"
            sx={{ p: 2, textAlign: "center", fontSize: "1.6rem" }}
          >
            {t("title")}
          </Typography>
          <>
            <DataGrid
              rows={usersTable}
              columns={columns}
              filterMode="client"
              disableColumnMenu={false}
              onRowClick={handleRowClick}
              loading={loading}
              sx={{
                cursor: "pointer",
                "& .MuiDataGrid-row:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              pagination
              paginationModel={{
                pageSize: rowsPerPage,
                page: page,
              }}
              onPaginationModelChange={({ page, pageSize }) => {
                setPage(page);
                setRowsPerPage(pageSize);
              }}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </>
        </Paper>
      </Box>

      <UserInfoModal
        open={openModal}
        setOpen={setOpenModal}
        selectedUser={selectedUser}
      />
      <Box sx={{ display: "flex", justifyContent: "center", gap: "3px" }}>
        <Typography sx={{ fontSize: "12px" }}>
          {t("biasCta.prefix")}{" "}
          <span
            style={{ color: "blue", cursor: "pointer" }}
            onClick={() => setOpenBias(true)}
          >
            {t("biasCta.here")}
          </span>{" "}
          {t("biasCta.suffix")}
        </Typography>
        <BiasAssessment open={openBias} handleClose={handleCloseBias} />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "3px",
          paddingTop: "3px",
        }}
      >
        <Typography sx={{ fontSize: "12px" }}>
          <strong>*</strong> {t("disclaimer")}
        </Typography>
      </Box>
    </Layout>
  );
}
